import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild, ViewContainerRef, computed, effect, inject, input, linkedSignal, model, signal, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { AnimationOption, OrientationOption, SlideType, SortingWidgetBucket, SortingWidgetOption } from '../../../common/models';


type SortingWidgetAnswer = {
  type: SlideType.SlideSorting;

  answers: {
    questionHash: string;
    bucket: string;
  }[];
}

type SortingWidgetOptionExtended = SortingWidgetOption & {
  index: number;
}

type SortingWidgetBucketExtended = SortingWidgetBucket & {
  droppedItems?: SortingWidgetOptionExtended[];
}


@Component({
  selector: 'lib-question-sorting-widget',
  templateUrl: './question-sorting-widget.component.html',
  styleUrl: './question-sorting-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QuestionSortingWidgetComponent implements AfterViewInit, OnDestroy {
  private readonly mobileViewportMediaQuery = '(max-width: 639px)';
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly overlayService = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly document = inject(DOCUMENT);

  @ViewChild('itemsOverlayTemplate') private itemsOverlayTemplate?: TemplateRef<unknown>;

  public answer = model<SortingWidgetAnswer | null>();
  public animation = input<AnimationOption>();
  public animationDelay = input<number>(0);
  public readonlyMode = input<boolean>(false);
  public buckets = input<SortingWidgetBucketExtended[]>();
  public items = input<SortingWidgetOption[]>();
  public orienation = input<OrientationOption>(OrientationOption.HORIZONTAL);

  public selectedItem = signal<SortingWidgetOptionExtended | null>(null);

  public readonly isMobileViewport = signal(this.breakpointObserver.isMatched(this.mobileViewportMediaQuery));
  public readonly isItemsOverlayCollapsed = signal(false);

  private itemsOverlayRef?: OverlayRef;
  private mobileViewportSubscription?: Subscription;

  public options = linkedSignal<SortingWidgetOptionExtended[]>(() =>
    this.items()?.map((item, i) => ({
      ...item,
      index: i
    })) ?? []
  );

  public bucketsState = linkedSignal(() =>
    this.buckets()?.map(bucket => ({
      ...bucket,
      droppedItems: bucket.droppedItems ?? [] as SortingWidgetOptionExtended[],
    })) ?? []
  );

  public itemsLeft = computed(() => {
    const assignedIds = new Set(
      this.bucketsState().flatMap(b => b.droppedItems.map(i => i.id))
    );

    return this.options()?.filter(item => !assignedIds.has(item.id));
  });

  public readonly hasVisibleItemsOverlay = computed(() =>
    this.isMobileViewport() && this.itemsLeft().length > 0
  );

  private readonly bodyOverlayBottomSpaceEffect = effect(() => {
    const bottomSpace = this.hasVisibleItemsOverlay() ? '52px' : '0px';
    this.document.body.style.setProperty('--lms-bottom-overlay-offset', bottomSpace);
  });

  private bucketsChanged = effect(() => {
    const state = this.bucketsState();
    const itemsLeft = this.itemsLeft();

    if (itemsLeft?.length > 0) {
      this.answer.set(null);
      return;
    }

    const answers  = state.reduce((total: { bucket: string; questionHash: string; }[], current) => {
      const options = current.droppedItems.map(option => ({
        bucket: current.id,
        questionHash: option.questionHash ?? option.id
      }))

      return [...total, ...options];
    }, []);

    this.answer.set({
      type: SlideType.SlideSorting,
      answers
    });
  });

  public ngAfterViewInit(): void {
    this.syncItemsTrayPlacement(this.isMobileViewport());

    this.mobileViewportSubscription = this.breakpointObserver
      .observe(this.mobileViewportMediaQuery)
      .subscribe(({ matches }) => {
        this.isMobileViewport.set(matches);
        this.syncItemsTrayPlacement(matches);
      });
  }

  public ngOnDestroy(): void {
    this.mobileViewportSubscription?.unsubscribe();
    this.itemsOverlayRef?.dispose();
    this.restoreBodyOverlayBottomSpace();
  }

  public toggleItemsOverlayCollapsed(): void {
    this.isItemsOverlayCollapsed.update(value => !value);
  }

  public updateOverlayCollapsedState(state: boolean): void {
    this.isItemsOverlayCollapsed.set(state);
  }

  public setSelectedItem(item: SortingWidgetOptionExtended, event?: Event): void {
    event?.stopPropagation();
    if (this.readonlyMode()) {
      return;
    }

    this.selectedItem.set(item);
  }

  public dropSelectedItemToBucket(bucketIndex: number = -1): void {
     if (this.readonlyMode()) {
      return;
    }
    
    const item = this.selectedItem();
  
    if (!item) {
      return;
    }

    const selectedItemBucket = this.bucketsState()
      .findIndex(bucket => bucket.droppedItems.find(dropped => dropped.id === item.id));

    this.moveItemBetweenBuckets(item, bucketIndex, selectedItemBucket);
    this.selectedItem.set(null);
  }

  public drop(event: CdkDragDrop<any>): void {
    const targetBucketIndex = event.container.data.index;
    const sourceBucketIndex = event.previousContainer.data.index;
    const item = event.item.data as SortingWidgetOptionExtended;

    this.moveItemBetweenBuckets(item, targetBucketIndex, sourceBucketIndex);
    this.selectedItem.set(null);
  }

  private moveItemBetweenBuckets(item: SortingWidgetOptionExtended, targetBucketIndex: number, sourceBucketIndex = -1): void {
    this.bucketsState.update(buckets => {
      const next = structuredClone(buckets);

      if (sourceBucketIndex >= 0) {
        next[sourceBucketIndex].droppedItems = next[sourceBucketIndex].droppedItems.filter(i => i.id !== item.id);
      }

      if (targetBucketIndex >= 0) {
        next[targetBucketIndex].droppedItems.unshift(item);
      }

      return next;
    });
  }

  private syncItemsTrayPlacement(isMobileViewport: boolean): void {
    if (!isMobileViewport) {
      this.itemsOverlayRef?.detach();
      this.isItemsOverlayCollapsed.set(false);
      return;
    }

    if (!this.itemsOverlayRef) {
      this.itemsOverlayRef = this.overlayService.create({
        width: '100%',
        maxWidth: '100%',
        positionStrategy: this.overlayService.position().global().bottom('0').centerHorizontally(),
        scrollStrategy: this.overlayService.scrollStrategies.noop(),
      });
    }

    if (!this.itemsOverlayRef.hasAttached() && this.itemsOverlayTemplate) {
      this.itemsOverlayRef.attach(new TemplatePortal(this.itemsOverlayTemplate, this.viewContainerRef));
    }
  }

  private restoreBodyOverlayBottomSpace(): void {
    this.document.body.style.removeProperty('--lms-bottom-overlay-offset');
  }
}
